<template>
    <div class="dashboard-employee-card-background">
        <b-button class="dashboard-employee-card-button-style" v-b-toggle.accordion-open-trajectories-overview>
            <div class="float-right">
                <span class="mr-2 badge badge-secondary">{{ trajectoryDataCount }}</span>
                <span class="mr-2 badge badge-danger">{{ trajectoryDataLateCount }}</span>
                <i v-if="this.open" class="fa fa-chevron-right" aria-hidden="true"></i>
                <i v-else class="fa fa-chevron-down" aria-hidden="true"></i>
            </div>
            {{ uLang('app.open_trajectories_overview') }}
        </b-button>

        <b-collapse class="dashboard-employee-card-collapse" id="accordion-open-trajectories-overview">
            <rea-loader :loading="loading"></rea-loader>

            <div class="d-flex align-items-center mt-3" v-if="hasTrajectoryData">
                <span class="mr-2">{{ uLang('app.rows_per_page_text') }}:</span>
                <select class="rowsPerPageClass" v-model.number="tablePageSize">
                    <option :value="15">15</option>
                    <option :value="30">30</option>
                    <option :value="45">45</option>
                    <option :value="60">60</option>
                    <option :value="90">90</option>
                </select>
            </div>

            <table class="overview-open-trajectories-table table mt-2">
                <tr v-if="trajectoryData.length === 0">
                    <td colspan="4">{{ uLang('app.empty_table') }}</td>
                </tr>

                <!-- Headers -->
                <tr v-if="trajectoryData.length > 0">
                    <th class="override-th-min-width"></th>
                    <th class="w10">{{ uLang('app.candidate') }}</th>
                    <th class="w20">{{ uLang('app.trajectory') }}</th>
                    <th class="w12 icon-sort-header" @click="sortTable('weeks_since_last_logbook')">
                        <span class="header-content">
                            {{ uLang('app.weeks_since_logbook') }}
                            <span class="icon-sort" :class="{
                                '-ascending': sortColumn === 'weeks_since_last_logbook' && sortOrder === 'asc',
                                '-descending': sortColumn === 'weeks_since_last_logbook' && sortOrder === 'desc'
                            }"></span>
                        </span>
                    </th>
                    <th class="w10">{{ uLang('app.trajectory_manager_literal') }}</th>
                    <th class="w10">{{ uLang('app.action_to_complete') }}</th>
                    <th class="w10 icon-sort-header" @click="sortTable('sent_on')">
                        <span class="header-content">
                            {{ uLang('app.sent_on') }}
                            <span class="icon-sort" :class="{
                                '-ascending': sortColumn === 'sent_on' && sortOrder === 'asc',
                                '-descending': sortColumn === 'sent_on' && sortOrder === 'desc'
                            }">
                            </span>
                        </span>
                    </th>
                    <th class="w15">{{ uLang('app.relation_to_do_action') }}</th>
                    <th class="icon-sort-header" @click="sortTable('due_date')">
                        <span class="header-content">
                            {{ uLang('app.deadline') }}
                            <span class="icon-sort" :class="{
                                '-ascending': sortColumn === 'due_date' && sortOrder === 'asc',
                                '-descending': sortColumn === 'due_date' && sortOrder === 'desc'
                            }">
                            </span>
                        </span>
                    </th>
                    <th class="w05">{{ uLang('app.reminder_logbook') }}</th>
                </tr>

                <!-- Filters -->
                <tr v-if="hasTrajectoryData">
                    <td colspan="2"></td>
                    <td>
                        <input
                            type="text"
                            class="form-control"
                            :placeholder="uLang('app.search_by_trajectory')"
                            v-model="filters.trajectory"
                        />
                    </td>
                    <td colspan=""></td>
                    <td>
                        <input
                            type="text"
                            class="form-control"
                            :placeholder="uLang('app.search_by_trajectory_manager')"
                            v-model="filters.trajectory_manager"
                        />
                    </td>
                    <td>
                        <input
                            type="text"
                            class="form-control"
                            :placeholder="uLang('app.search_by_action_to_complete')"
                            v-model="filters.trajectory_action_to_complete"
                        />
                    </td>
                    <td colspan=""></td>
                    <td>
                        <input
                            type="text"
                            class="form-control"
                            :placeholder="uLang('app.search_by_follow_up')"
                            v-model="filters.follow_up"
                        />
                    </td>
                    <td colspan=""></td>
                    <td colspan="">
                        <span class="reminder_type">R1</span>
                        <span class="reminder_type">R2</span>
                        <span class="reminder_type">IG</span>
                        <span class="reminder_type">NvT</span>
                </td>
                </tr>

                <!-- Rows -->
                <tr v-for="enrolment in pagedTrajectories" :key="enrolment.id">
                    <td>
                        <i class="ti-email pointer"
                        @click.prevent="sendLogToRelation(enrolment.relation)"
                        ></i>
                    </td>
                    <td>
                        <name-telephone-field
                            :relation="enrolment.relation"
                            :permission_link="true"/>
                    </td>
                    <td>
                        <link-if-permissions
                            target="_blank"
                            permissions="view-trajectory-enrolments"
                            :to="{
                                name: 'admin.relations.trajectory-enrolments.view',
                                params: {
                                    trajectoryEnrolmentId: enrolment.id,
                                    relationId: enrolment.relation_id,
                                }
                            }"
                        >
                            {{ $getIfExists(enrolment, 'trajectory.name') }}
                        </link-if-permissions>
                    </td>
                    <td>
                        <logbook-tooltip-field
                            :weeks="enrolment.weeks_since_last_logbook"
                            :relation="enrolment.relation"
                            :permission_link="true"
                            :logbook="$getIfExists(enrolment, 'relation.latest_logbook', null)"
                        />
                    </td>
                    <td>
                        <name-telephone-field
                            :relation="enrolment.trajectory_manager"
                            :permission_link="true"/>
                    </td>
                    <td>
                        <!-- @todo: add proper permission here -->
                        <router-link
                            target="_blank"
                            v-if="enrolment.latest_active_milestone_progress.latest_active_action_progress"
                            :to="{
                                name: 'admin.relations.trajectory-enrolments.milestone-progresses.action-progresses.action-document.update',
                                params: {
                                    relationId: enrolment.relation_id,
                                    trajectoryEnrolmentId: enrolment.id,
                                    milestoneProgressId: enrolment.latest_active_milestone_progress.id,
                                    actionProgressId: enrolment.latest_active_milestone_progress.latest_active_action_progress.id,
                                }
                            }"
                        >
                            {{
                                $getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.action.name', 'Actie niet gevonden')
                            }}
                        </router-link>
                        <span v-else>
                            {{
                                $getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.action.name', 'Actie niet gevonden')
                            }}
                        </span>
                    </td>
                    <td>
                        {{
                            enrolment.latest_active_milestone_progress.latest_active_action_progress.created_at | date
                        }}
                    </td>
                    <td>
                        <span v-if="shouldGetRelationName(enrolment)">
                        <link-if
                            :if="$getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.action.assigned_to_role.system_name') === 'trajectory_manager'"
                            :to="{
                                name: 'admin.relations.view',
                                params: {
                                    id: enrolment.trajectory_manager_id,
                                }
                            }"
                            target="_blank"
                        >
                            {{ getRelationNameBasedOnRole(enrolment) }}
                        </link-if>
                        </span>
                        <span v-else>
                        <name-telephone-field
                            :relation="getRelationObjectBasedOnRole(enrolment)"
                            :permission_link="true"/>
                        </span>
                    </td>
                    <td>
                        <span
                            v-if="$getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.due_date', '') !== ''">
                            {{
                                moment(enrolment.latest_active_milestone_progress.latest_active_action_progress.due_date).fromNow()
                            }}
                        </span>
                        <span v-else>
                            Niet te bepalen
                        </span>
                    </td>
                    <td>
                        <logbook-reminder-checkbox
                            type="R1"
                            :trajectory_enrolment="enrolment"
                            :action_progress="enrolment.latest_active_milestone_progress.latest_active_action_progress"
                            :relation="enrolment.relation"
                            :logbook="logbookByType(enrolment.relation, 'R1')"
                            @logCreated="onReminderLogCreated(
                                enrolment.relation,
                                enrolment.latest_active_milestone_progress.latest_active_action_progress,
                                $event)"

                        />

                        <logbook-reminder-checkbox
                            type="R2"
                            :trajectory_enrolment="enrolment"
                            :action_progress="enrolment.latest_active_milestone_progress.latest_active_action_progress"
                            :relation="enrolment.relation"
                            :logbook="logbookByType(enrolment.relation, 'R2')"
                            @logCreated="onReminderLogCreated(
                                enrolment.relation,
                                enrolment.latest_active_milestone_progress.latest_active_action_progress,
                                $event)"
                        />

                        <logbook-reminder-checkbox
                            type="IG"
                            :trajectory_enrolment="enrolment"
                            :action_progress="enrolment.latest_active_milestone_progress.latest_active_action_progress"
                            :relation="enrolment.relation"
                            :logbook="logbookByType(enrolment.relation, 'IG')"
                            @logCreated="onReminderLogCreated(
                                enrolment.relation,
                                enrolment.latest_active_milestone_progress.latest_active_action_progress,
                                $event)"
                        />

                        <logbook-reminder-checkbox
                            type="NvT"
                            :trajectory_enrolment="enrolment"
                            :action_progress="enrolment.latest_active_milestone_progress.latest_active_action_progress"
                            :relation="enrolment.relation"
                            :logbook="logbookByType(enrolment.relation, 'NvT')"
                            @logCreated="onReminderLogCreated(
                                enrolment.relation,
                                enrolment.latest_active_milestone_progress.latest_active_action_progress,
                                $event)"
                        />
                    </td>
                </tr>
            </table>

            <div class="VuePagination" v-if="hasTrajectoryData && overviewMeta.total > 0">
                <ul class="pagination VuePagination__pagination" v-show="totalPages > 1">
                    <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-prev-chunk"
                        :class="{ disabled: currentChunk <= 1 }">
                        <a class="page-link" href="javascript:void(0);" @click="goPrevChunk">&lt;&lt;</a>
                    </li>
                    <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-prev-page"
                        :class="{ disabled: overviewMeta.current_page <= 1 }">
                        <a class="page-link" href="javascript:void(0);" @click="tablePage = overviewMeta.current_page - 2">&lt;</a>
                    </li>
                    <li v-for="p in chunkPageNumbers" :key="p"
                        class="VuePagination__pagination-item page-item"
                        :class="{ active: overviewMeta.current_page === p }">
                        <a class="page-link" role="button" @click="tablePage = p - 1">{{ p }}</a>
                    </li>
                    <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-next-page"
                        :class="{ disabled: overviewMeta.current_page >= totalPages }">
                        <a class="page-link" href="javascript:void(0);" @click="tablePage = overviewMeta.current_page">&gt;</a>
                    </li>
                    <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-next-chunk"
                        :class="{ disabled: currentChunk >= totalChunks }">
                        <a class="page-link" href="javascript:void(0);" @click="goNextChunk">&gt;&gt;</a>
                    </li>
                </ul>
                <p class="VuePagination__count">{{ paginationCount }}</p>
            </div>

        </b-collapse>
        <b-modal
            class="log-modal-lg"
            :close-on-esc="false"
            :close-on-backdrop="false"
            :hideFooter="true"
            :title="uLang('app.add_type', { type: uLang('app.log') })"
            :key="log_form_key"
            ref="relationLogsModal"
            size="lg"
            v-if="($can('create-intern-logs')
                    || $can('update-intern-logs')
                    || $can('create-report-logs')
                    || $can('update-report-logs')
                    || $can('create-candidate-logs')
                    || $can('update-candidate-logs'))
            && relation_id !== null"
        >
            <log-form
                @logCreated="handleLogCreated"
                @categoryChanged="handleCategoryChanged"
                entityType="relations"
                :entityId="relation_id"
                :overrideEndpoint="logsFormApiUrl"
            />
        </b-modal>

    </div>
</template>
<script>
import {LogForm} from '~/components/forms'
import {ReaLoader} from '~/components/helpers'
import NameTelephoneField from "../../../elements/name-telephone-field";
import LogbookTooltipField from "../../../elements/logbook-tooltip-field";
import LogbookReminderCheckbox from "../../../elements/logbook-reminder-checkbox";
import {debounce} from "lodash";
import {DashboardFilter} from "~/mixins";

export default {
    name: 'OverviewOpenTrajectories',

    components: {
        NameTelephoneField,
        LogbookTooltipField,
        LogbookReminderCheckbox,
        ReaLoader,
        LogForm
    },

    mixins: [
        DashboardFilter
    ],

    data() {
        return {
            category_id: null,
            relation_id: null,
            log_form_key: 0,
            trajectoryData: [],
            loading: true,
            open: false,
            trajectoryDataCount: 0,
            trajectoryDataLateCount: 0,
            overviewMeta: { total: 0, per_page: 15, current_page: 1, last_page: 1 },

            sortColumn: 'due_date',
            sortOrder: 'asc',

            tablePage: 0,
            tablePageSize: 15,

            filters: {
                trajectory: '',
                trajectory_manager: '',
                trajectory_action_to_complete: '',
                follow_up: '',
            },
        }
    },

    async created() {
        this.$root.$on('collapse::toggle', async (target, show) => {
            if (target == 'accordion-open-trajectories-overview') {
                this.open = !this.open;
            }
        });
        this.$root.$on('collapse::toggle::state', async (target, show) => {
            if (target == 'accordion-open-trajectories-overview') {
                if (show == true && this.loading == true) {
                    await this.fetchOverview();
                    this.loading = false;
                }
            }
        });

        const { data } = await window.axios.get(route("dashboard.overview-open-trajectories.count"));
        this.trajectoryDataCount = data.overview_open_trajectories_count.count;
        this.trajectoryDataLateCount = data.overview_open_trajectories_count.due_count;
    },

    watch: {
        tablePage: function (newPage) {
            const requestedPage = newPage + 1;
            if (!this.loading && this.open && this.overviewMeta.current_page !== requestedPage) {
                this.loading = true;
                this.fetchOverview().then(() => { this.loading = false; });
            }
        },
        tablePageSize: function () {
            this.tablePage = 0;
            if (!this.loading && this.open) {
                this.loading = true;
                this.fetchOverview().then(() => { this.loading = false; });
            }
        },
        'filters.trajectory': debounce(function () { this.tablePage = 0; this.refetchOverview(); }, 500),
        'filters.trajectory_manager': debounce(function () { this.tablePage = 0; this.refetchOverview(); }, 500),
        'filters.trajectory_action_to_complete': debounce(function () { this.tablePage = 0; this.refetchOverview(); }, 500),
        'filters.follow_up': debounce(function () { this.tablePage = 0; this.refetchOverview(); }, 500),
        sortColumn: function () {
            this.tablePage = 0;
            if (this.open && !this.loading) this.refetchOverview();
        },
        sortOrder: function () {
            this.tablePage = 0;
            if (this.open && !this.loading) this.refetchOverview();
        },
    },

    computed: {
        // sortedTrajectoryData() {
        //     return this.trajectoryData.sort(function (a, b) {
        //         return new Date(a.latest_active_milestone_progress.latest_active_action_progress.due_date) - new Date(b.latest_active_milestone_progress.latest_active_action_progress.due_date)
        //     })
        // },
        logsFormApiUrl() {
            return route('logs.store', {
                category: this.category_id,
            })
        },
        hasTrajectoryData() {
            return !_.isEmpty(this.trajectoryData) || this.overviewMeta.total > 0;
        },
        pagedTrajectories() {
            return this.trajectoryData;
        },
        totalPages() {
            return Math.max(1, this.overviewMeta.last_page || 1);
        },
        paginationStart() {
            const total = this.overviewMeta.total || 0;
            if (total === 0) return 0;
            return (this.overviewMeta.current_page - 1) * this.overviewMeta.per_page + 1;
        },
        paginationEnd() {
            return Math.min(
                this.overviewMeta.current_page * this.overviewMeta.per_page,
                this.overviewMeta.total || 0
            );
        },
        currentPage() {
            return this.overviewMeta.current_page || 1;
        },
        currentChunk() {
            return Math.ceil(this.currentPage / 5);
        },
        totalChunks() {
            return Math.ceil(this.totalPages / 5);
        },
        chunkStart() {
            return (this.currentChunk - 1) * 5 + 1;
        },
        chunkPageNumbers() {
            const count = this.chunkStart + 5 <= this.totalPages
                ? 5
                : this.totalPages - this.chunkStart + 1;
            return Array.from({ length: count }, (_, i) => this.chunkStart + i);
        },
        paginationCount() {
            const total = this.overviewMeta.total || 0;
            if (total === 0) return '';
            if (total === 1) return '1 resultaat';
            if (this.totalPages === 1) return `${total} resultaten`;
            return `${this.paginationStart} tot ${this.paginationEnd} van de ${total} resultaten`;
        },
    },

    methods: {
        async fetchOverview() {
            const params = {
                page: this.tablePage + 1,
                per_page: this.tablePageSize,
                sort_column: this.sortColumn || 'due_date',
                sort_order: this.sortOrder,
            };
            if (this.filters.trajectory) params.trajectory = this.filters.trajectory;
            if (this.filters.trajectory_manager) params.trajectory_manager = this.filters.trajectory_manager;
            if (this.filters.trajectory_action_to_complete) params.trajectory_action_to_complete = this.filters.trajectory_action_to_complete;
            if (this.filters.follow_up) params.follow_up = this.filters.follow_up;

            const { data } = await window.axios.get(route("dashboard.overview-open-trajectories"), { params });
            this.trajectoryData = data.trajectory_administrator.overview_open_trajectories || [];
            this.overviewMeta = data.trajectory_administrator.overview_open_trajectories_meta || this.overviewMeta;
        },
        refetchOverview() {
            if (!this.open) return;
            this.loading = true;
            this.fetchOverview().then(() => { this.loading = false; });
        },
        sendLogToRelation(relation) {
            this.log_form_key += 1
            this.relation_id = relation.id
            setTimeout(() => {
                this.$refs.relationLogsModal.show()
            }, 500)

        },

        shouldGetRelationName(enrolment) {
            return !['trajectory_manager', 'candidate'].includes($getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.action.assigned_to_role.system_name', false))
        },

        getRelationObjectBasedOnRole(enrolment) {
            switch ($getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.action.assigned_to_role.system_name', 'role_not_found')) {
                case 'trajectory_manager' :
                    return $getIfExists(enrolment, 'trajectory_manager', {});
                case 'candidate' :
                    return $getIfExists(enrolment, 'relation', {});
            }

            return {}
        },

        getRelationNameBasedOnRole(enrolment) {
            switch ($getIfExists(enrolment, 'latest_active_milestone_progress.latest_active_action_progress.action.assigned_to_role.system_name', 'role_not_found')) {
                case 'super_admin' :
                    return 'Mijn id Plein administrateur';
                case 'trajectory_manager' :
                    return $getIfExists(enrolment, 'trajectory_manager.full_name', 'De coach');
                case 'trajectory_inspector' :
                    return 'Een trajectcontroleur';
                case 'trajectory_administrator' :
                    return 'Een traject administrateur';
                case 'project_manager' :
                    return 'Een project manager';
                case 'contact' :
                    return 'De rapportage persoon';
                case 'action_taker' :
                    return 'De actienemer';
                case 'candidate' :
                    return $getIfExists(enrolment, 'relation.full_name', 'De kandidaat');
                default:
                    return uLang('app.no_role_found');
            }
        },

        openLogToRelation(trajectoryEnrolment) {
            let routeData = this.$router.resolve({
                name: 'admin.relations.view', params: {
                    'id': trajectoryEnrolment.relation_id
                }
            });
            window.open(routeData.href, '_blank');
        },

        handleLogCreated() {
            this.$refs.relationLogsModal.hide();
            this.forceRefresh = true
        },

        handleCategoryChanged(category_id) {
            this.category_id = category_id
        },

        stopRefreshing() {
            this.forceRefresh = false
        },

        goPrevChunk() {
            if (this.currentChunk > 1) {
                this.tablePage = (this.currentChunk - 2) * 5;
            }
        },
        goNextChunk() {
            if (this.currentChunk < this.totalChunks) {
                this.tablePage = this.currentChunk * 5;
            }
        },

        sortTable(column) {
            this.tablePage = 0;
            if (this.sortColumn === column) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortColumn = column;
                this.sortOrder = 'asc';
            }
        },

        getFollowUpDisplayText(enrolment) {
            if (this.shouldGetRelationName(enrolment)) {
                return (this.getRelationNameBasedOnRole(enrolment) || '');
            }
            const rel = this.getRelationObjectBasedOnRole(enrolment);
            return (rel?.full_name || rel?.name || '');
        },

        logbookTooltip(enrolment) {
            const lb = enrolment?.relation?.latest_logbook;

            if (!lb) {
                return 'Geen logboek gevonden';
            }

            const subject = (lb.subject || '').toString().trim();
            const body    = (lb.body || '').toString().trim();

            const safe = (s) =>
                s
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');

            // because we use .html in the tooltip directive:
            return `<strong>Onderwerp:</strong> ${safe(subject || '-')}`
                + `<br><strong>Tekst:</strong> ${safe(body || '-')}`;
        },

        onReminderLogCreated(relation, actionProgress, log) {
            console.log('onReminderLogCreated', relation, log);

            if (!relation || !actionProgress || !log) return;

            // logbooks for tooltip, createdBy and createdAt
            if (!Array.isArray(relation.logbooks)) {
                this.$set(relation, 'logbooks', []);
            }

            // for only one per type, replace; otherwise push:
            const t = String(log.reminder_type || '').trim();
            relation.logbooks = relation.logbooks.filter(l => String(l.reminder_type || '') !== t);
            relation.logbooks.push(log);

            // update reminders array on action_progress (for isChecked in checkbox component)
            if (!Array.isArray(actionProgress.reminders)) {
                this.$set(actionProgress, 'reminders', []);
            }

            if (t && !actionProgress.reminders.includes(t)) {
                actionProgress.reminders = [...actionProgress.reminders, t];
            }

            console.log('Updated relation logbooks:', relation.logbooks);

            // Optional: for only one per type, replace
            // relation.logbooks = relation.logbooks.filter(l => String(l.reminder_type) !== String(log.reminder_type));
            // relation.logbooks.push(log);
        },


        logbookByType(relation, type) {
            const logs = relation?.logbooks || [];
            return logs.find(l => (l.reminder_type || '').toString() === type) || null;
        },

    }
}
</script>
<style lang="scss" scoped>
    .rowsPerPageClass {
        display: inline-block;
        width: 80px;
        padding: 4px 6px;
    }

    table.overview-open-trajectories-table {
        tr:first-child {
            td, th {
                text-align: left;
                border-top: none;
            }
        }
        td, th {
            padding-left: 0px !important;
        }

    }
    .action_icon {
        color: #5A4587 !important;
    }

    .action_icon:hover {
        color: lighten(#5A4587, 25%) !important;
    }

    .card-block {
        position: relative;
        min-height: 75px;
    }

    .override-th-min-width {
        min-width: 10px;
    }

    .w05 {
        width: 5%;
    }

    .w10 {
        width: 10%;
    }

    .w12 {
        width: 12%;
    }

    .w15 {
        width: 15%;
    }

    .w20 {
        width: 20%;
    }

    .w30 {
        width: 30%;
    }

    .w40 {
        width: 40%;
    }

</style>
<style lang="scss" scoped>
    table.action-documents-table {
        tr:first-child {
            th.icon-sort-header {
                padding-right: 14px;
                cursor: pointer;
            }
            td, th {
                text-align: left;
                border-top: none;
            }
            td:nth-child(6), th:nth-child(6) {
                text-align: center;
            }
        }
        td, th {
            padding-left: 0px !important;
        }
        td:nth-child(6), th:nth-child(6) {
            text-align: center;
        }
    }
    .action_icon {
        color: #5A4587 !important;
    }

    .action_icon:hover {
        color: lighten(#5A4587, 25%) !important;
    }

    .card-block {
        position: relative;
        min-height: 75px;
    }

    .override-th-min-width {
        min-width: 10px;
    }

    .table-heading {
        width: 100%;
    }

    .w10 {
        width: 10%
    }

    .w30 {
        width: 30%
    }

    .header-content {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    .icon-sort {
        position: relative;
        margin-left: 3px;
        display: inline-block;
        width: 10px;
        height: 10px;
    }

    .icon-sort::before, .icon-sort::after {
    content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 0; 
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
    }

    .icon-sort::before {
        top: 0%;
        border-bottom: 5px solid #808080;
    }

    .icon-sort::after {
        top: 60%;
        border-top: 5px solid #808080;
    }

    .icon-sort.-ascending::before {
        border-bottom: 5px solid #c1006d;
    }

    .icon-sort.-descending::after {
        border-top: 5px solid #c1006d;
    }

    span.reminder_type {
        font-size: 0.8em;
        font-weight: 600;
        line-height: 3.4em;
    }
</style>
